import { Button } from "@mui/material";
import { useEffect, useState } from "react";
import { handleCreatePermissionsApi, handleDeletePermission, handleGetPermissionByCompanyIdApi } from "../../../api/mosaic/roles";
import PermissionsTable, { getInitialPermissions, IPermissionItems, PERMISSIONS } from "./PermissionsTable";
import { useToast } from "../../shared/Toast";
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from "../../shared/ModalWrapper";

interface IUpdateCompanyPermissionsModal {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDoneSaving: React.Dispatch<React.SetStateAction<boolean>>;
  rowData?: React.SetStateAction<any>;
}

const UpdateCompanyPermissionsModal = ({
  open,
  setOpen,
  setIsDoneSaving,
  rowData,
}: IUpdateCompanyPermissionsModal) => {
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [updatedPermissions, setUpdatedPermissions] = useState<Record<string, IPermissionItems>>(getInitialPermissions());

  const handleClose = () => setOpen(false);

  const handleOnChange = (_e: React.ChangeEvent<HTMLInputElement>, permissionName: string) => {
    setUpdatedPermissions((prev) => ({
      ...prev,
      [permissionName]: { ...prev[permissionName], enabled: !prev[permissionName].enabled }
    }));
  };

  const handleUpdatePermissions = async () => {
    try {
      await Promise.all(
        Object.keys(PERMISSIONS).map(async (key) => {
          if (updatedPermissions[key].enabled) {
            if (!updatedPermissions[key].id) {
              await handleCreatePermissionsApi({ name: updatedPermissions[key].name, companyId: rowData.id });
            }
          } else {
            if (updatedPermissions[key].id) {
              await handleDeletePermission(updatedPermissions[key].id as number);
            }
          }
        })
      );
      setIsDoneSaving((prev) => !prev);
      setOpen(false);
      showSuccess("Permissions updated successfully");
    } catch (err) {
      showError("Error updating permissions");
      console.error(err);
    }
  };

  useEffect(() => {
    const getPermissions = async () => {
      setIsLoading(true);
      const permissions = await handleGetPermissionByCompanyIdApi(rowData.id);
      const permissionsUpdated = getInitialPermissions();
      permissions.forEach((permission) => {
        permissionsUpdated[permission.name] = { ...permissionsUpdated[permission.name], enabled: true, id: permission.id };
      });
      setUpdatedPermissions(permissionsUpdated);
      setIsLoading(false);
    };
    getPermissions();
  }, [rowData]);

  return (
    <ModalWrapper
      open={open}
      onClose={handleClose}
      title={`${rowData.company_name}: Update Permissions`}
      maxWidth="md"
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} sx={modalCancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpdatePermissions} disabled={isLoading} sx={modalSubmitButtonSx}>
            {isLoading ? "Loading..." : "Update"}
          </Button>
        </>
      }
    >
      <PermissionsTable permissions={updatedPermissions} onToggle={handleOnChange} />
    </ModalWrapper>
  );
};

export default UpdateCompanyPermissionsModal;
